/**
 * MI DINERO — «¿Dónde está mi dinero y cuánto tengo?»
 *
 * Las cuentas bancarias vivían dentro de PERFIL. Lo más central del producto
 * estaba en el cajón de la configuración, y el Home mandaba ahí con un botón
 * que decía «Gestionar». Eso termina aquí.
 *
 * Esta pantalla reúne: cuentas, tarjetas (como deuda, que es lo que son),
 * metas, límites de gasto y el patrimonio. Perfil vuelve a ser nombre, correo,
 * hogar, suscripción y preferencias — nada de dinero.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { track } from "@/lib/productTelemetry";
import { money, humanErrorLine } from "@/lib/human";
import { toast } from "sonner";
import { Plus, CreditCard, Target, Gauge, ChevronRight, Wallet, Banknote, TrendingUp } from "lucide-react";

export default function MyMoneyPage() {
  useEffect(() => { track("my_money_viewed", { screen: "my_money" }); }, []);
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [cards, setCards] = useState([]);
  const [goals, setGoals] = useState([]);
  const [budgets, setBudgets] = useState([]);

  const load = useCallback(async () => {
    try {
      const [h, c, g, b] = await Promise.all([
        api.get("/overview/home"),
        api.get("/cards"),
        api.get("/goals").catch(() => ({ data: [] })),
        api.get("/budgets").catch(() => ({ data: [] })),
      ]);
      setData(h.data);
      setCards((c.data || []).filter(x => x.kind === "credit"));
      setGoals(g.data || []);
      setBudgets(b.data || []);
    } catch (e) { toast.error(humanErrorLine(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener("td:data-changed", load);
    return () => window.removeEventListener("td:data-changed", load);
  }, [load]);

  const cur = data?.base_currency || "DOP";

  // El servidor ya dijo a qué montón pertenece cada cuenta (`purpose`). Aquí
  // sólo se agrupa: si esta pantalla volviera a decidirlo, tendríamos otra vez
  // dos versiones de la misma regla.
  const todas = data?.accounts || [];
  const paraUsar = todas.filter(a => a.purpose !== "INVESTMENT" && a.purpose !== "OTHER");
  // Agrupadas por divisa: una lista plana con RD$4,022 encima de US$53 invita a
  // leerlas como la misma escala, y no lo son.
  const NOMBRE_DIVISA = { DOP: "Pesos", USD: "Dólares", EUR: "Euros", USDT: "USDT" };
  const porDivisa = (lista) => Object.entries(
    lista.reduce((acc, a) => {
      (acc[a.currency] = acc[a.currency] || []).push(a);
      return acc;
    }, {})).sort(([a], [b]) => (a === cur ? -1 : b === cur ? 1 : a.localeCompare(b)));
  const inversiones = todas.filter(a => a.purpose === "INVESTMENT");
  const otras = todas.filter(a => a.purpose === "OTHER");

  return (
    <div className="space-y-6 pb-4" data-testid="my-money-page">
      <header>
        <h1 className="text-2xl font-bold text-white">Mi dinero</h1>
      </header>

      <section className="rounded-3xl bg-white/[0.04] border border-white/10 p-6" data-testid="my-money-total">
        <h2 className="text-[13px] uppercase tracking-[0.16em] text-slate-400 font-bold">Para usar</h2>
        {Object.entries(data?.can_use?.by_currency || { [cur]: data?.can_use?.liquidity ?? 0 })
          .sort(([a], [b]) => (a === cur ? -1 : b === cur ? 1 : a.localeCompare(b)))
          .map(([divisa, monto], i) => (
            <p key={divisa} data-testid={`my-money-total-${divisa}`}
              className={`font-bold text-white tabular-nums ${
                i === 0 ? "mt-1.5 text-4xl" : "mt-1 text-2xl text-white/90"}`}>
              {money(monto, divisa)}
            </p>
          ))}
        {(data?.owe?.total ?? 0) > 0 && (
          <p className="mt-3 text-[14px] text-slate-400">
            Y debes <strong className="text-white font-semibold">{money(data.owe.total, cur)}</strong>.
          </p>
        )}
      </section>

      {/* CUENTAS — aquí es donde ahora se administran */}
      <section data-testid="my-money-accounts">
        <Header
          title="Para usar"
          action={{ label: "Agregar", onClick: () => nav("/mi-dinero/cuentas") }} />
        {(data?.accounts || []).length === 0 ? (
          <Empty cta={{ label: "Agregar una cuenta", onClick: () => nav("/mi-dinero/cuentas") }}>
            Dile a la app dónde guardas tu dinero: una cuenta del banco, o el efectivo que
            cargas encima.
          </Empty>
        ) : (
          porDivisa(paraUsar).map(([divisa, cuentas]) => (
          <div key={divisa} className="mb-4 last:mb-0">
            <h3 className="text-[12px] uppercase tracking-[0.14em] text-slate-500 font-bold mb-2"
              data-testid={`my-money-group-${divisa}`}>
              {NOMBRE_DIVISA[divisa] || divisa}
            </h3>
          <ul className="space-y-2">
            {cuentas.map(a => (
              <li key={a.id}>
                <button onClick={() => nav("/mi-dinero/cuentas")} data-testid={`money-account-${a.id}`}
                  className="w-full flex items-center gap-3 p-4 rounded-2xl bg-white/[0.04] border border-white/10 text-left min-h-16 hover:bg-white/[0.07]">
                  <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: `${a.color || "#8B5CF6"}22` }}>
                    {a.type === "cash"
                      ? <Banknote className="w-[18px] h-[18px] text-slate-200" aria-hidden="true" />
                      : <Wallet className="w-[18px] h-[18px] text-slate-200" aria-hidden="true" />}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[15px] font-semibold text-white truncate">{a.name}</span>
                    <span className="block text-[12px] text-slate-500">{a.currency}</span>
                  </span>
                  <span className="text-[16px] font-bold text-white tabular-nums shrink-0">
                    {money(a.balance, a.currency)}
                  </span>
                  <ChevronRight className="w-4 h-4 text-slate-600 shrink-0" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
          </div>
          ))
        )}
      </section>

      {/* INVERSIONES — tuyas, pero no para pagar la luz */}
      {inversiones.length > 0 && (
        <section data-testid="my-money-investments">
          <Header title="Inversiones" />
          <ul className="space-y-2">
            {inversiones.map(a => (
              <li key={a.id}>
                <button onClick={() => nav(`/movimientos?account=${a.id}`)}
                  data-testid={`money-investment-${a.id}`}
                  className="w-full flex items-center gap-3 p-4 rounded-2xl bg-white/[0.04] border border-white/10 text-left min-h-16 hover:bg-white/[0.07]">
                  <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: `${a.color || "#8B5CF6"}22` }}>
                    <TrendingUp className="w-[18px] h-[18px] text-slate-200" aria-hidden="true" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[15px] font-semibold text-white truncate">{a.name}</span>
                    <span className="block text-[12px] text-slate-500">{a.currency}</span>
                  </span>
                  <span className="text-[16px] font-bold text-white tabular-nums shrink-0">
                    {money(a.balance, a.currency)}
                  </span>
                  <ChevronRight className="w-4 h-4 text-slate-600 shrink-0" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
          <p className="mt-2.5 text-[13px] text-slate-400">
            Esto no se cuenta en «Para usar».
          </p>
        </section>
      )}

      {/* OTROS — lo que todavía no está clasificado */}
      {otras.length > 0 && (
        <section data-testid="my-money-other">
          <Header title="Otros" />
          <ul className="space-y-2">
            {otras.map(a => (
              <li key={a.id}>
                <button onClick={() => nav("/mi-dinero/cuentas")}
                  data-testid={`money-other-${a.id}`}
                  className="w-full flex items-center gap-3 p-4 rounded-2xl bg-white/[0.04] border border-white/10 text-left min-h-16 hover:bg-white/[0.07]">
                  <span className="flex-1 min-w-0">
                    <span className="block text-[15px] font-semibold text-white truncate">{a.name}</span>
                    <span className="block text-[12px] text-slate-500">{a.currency}</span>
                  </span>
                  <span className="text-[16px] font-bold text-white tabular-nums shrink-0">
                    {money(a.balance, a.currency)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* TARJETAS — como lo que son: deuda */}
      <section data-testid="my-money-cards">
        <Header title="Lo que debes en tarjetas"
          action={{ label: "Agregar", onClick: () => nav("/tarjetas") }} />
        {cards.length === 0 ? (
          <Empty cta={{ label: "Agregar una tarjeta", onClick: () => nav("/tarjetas") }}>
            Si usas tarjeta de crédito, agrégala para que tus compras cuenten y sepas
            cuándo toca pagarla.
          </Empty>
        ) : (
          <ul className="space-y-2">
            {cards.map(c => (
              <li key={c.id}>
                <button onClick={() => nav(`/movimientos?card=${c.id}`)} data-testid={`money-card-${c.id}`}
                  className="w-full flex items-center gap-3 p-4 rounded-2xl bg-white/[0.04] border border-white/10 text-left min-h-16 hover:bg-white/[0.07]">
                  <span className="w-10 h-10 rounded-xl bg-white/[0.07] flex items-center justify-center shrink-0">
                    <CreditCard className="w-[18px] h-[18px] text-slate-300" aria-hidden="true" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[15px] font-semibold text-white truncate">
                      {c.name} ••••{c.last4}
                    </span>
                    <span className="block text-[12px] text-slate-500">
                      {c.payment_due_day ? `se paga el día ${c.payment_due_day}` : "sin fecha de pago"}
                    </span>
                  </span>
                  <span className="text-[16px] font-bold text-white tabular-nums shrink-0">
                    {money(c.balance_used || 0, c.currency)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* METAS — «quiero ahorrar para...» */}
      <section data-testid="my-money-goals">
        <Header title="Estás ahorrando para"
          action={{ label: "Agregar", onClick: () => nav("/metas") }} />
        {goals.length === 0 ? (
          <Empty cta={{ label: "Crear una meta", onClick: () => nav("/metas") }}>
            ¿Ahorrando para un viaje, un carro, la casa? Ponlo aquí y ve cuánto llevas.
          </Empty>
        ) : (
          <ul className="space-y-2">
            {goals.map(g => {
              const pct = Math.min(100, Math.round((Number(g.saved || 0) / Math.max(1, Number(g.target || 1))) * 100));
              return (
                <li key={g.id} className="p-4 rounded-2xl bg-white/[0.04] border border-white/10"
                  data-testid={`money-goal-${g.id}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[15px] font-semibold text-white truncate flex items-center gap-2">
                      <Target className="w-4 h-4 text-violet-300 shrink-0" aria-hidden="true" />{g.name}
                    </span>
                    <span className="text-[14px] text-slate-300 tabular-nums shrink-0">
                      {money(g.saved, cur)} de {money(g.target, cur)}
                    </span>
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden"
                    role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}
                    aria-label={`Progreso de ${g.name}`}>
                    <div className="h-full rounded-full bg-violet-400" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="mt-1.5 text-[12px] text-slate-500">Llevas el {pct}%</p>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* LÍMITES — «quiero gastar máximo...». Antes se llamaba "Presupuestos" y
          compartía la pantalla "Metas" con lo de arriba, que es otra cosa. */}
      <section data-testid="my-money-limits">
        <Header title="Cuánto quieres gastar como máximo"
          action={{ label: "Agregar", onClick: () => nav("/metas") }} />
        {budgets.length === 0 ? (
          <Empty cta={{ label: "Poner un límite", onClick: () => nav("/metas") }}>
            Ponle un tope a lo que gastas en algo —comida, salidas— y la app te avisa
            cuando te estés acercando.
          </Empty>
        ) : (
          <ul className="space-y-2">
            {budgets.map(b => (
              <li key={b.id} className="flex items-center gap-3 p-4 rounded-2xl bg-white/[0.04] border border-white/10 min-h-14"
                data-testid={`money-limit-${b.id}`}>
                <Gauge className="w-4 h-4 text-slate-400 shrink-0" aria-hidden="true" />
                <span className="flex-1 text-[15px] text-white truncate">{b.category}</span>
                <span className="text-[14px] text-slate-300 tabular-nums shrink-0">
                  máximo {money(b.limit, cur)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {data && (
        <section className="rounded-2xl border border-white/10 p-5" data-testid="my-money-net-worth">
          <h2 className="text-[13px] text-slate-400">Si vendieras todo y pagaras todo lo que debes</h2>
          <p className="mt-1 text-2xl font-bold text-white tabular-nums">{money(data.net_worth, cur)}</p>
        </section>
      )}
    </div>
  );
}

function Header({ title, action }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-[15px] font-semibold text-white">{title}</h2>
      {action && (
        <button onClick={action.onClick}
          className="flex items-center gap-1 text-[13px] font-semibold text-violet-300 hover:text-violet-200 min-h-11">
          <Plus className="w-3.5 h-3.5" aria-hidden="true" />{action.label}
        </button>
      )}
    </div>
  );
}

function Empty({ children, cta }) {
  return (
    <div className="rounded-2xl border border-dashed border-white/15 p-5 text-center">
      <p className="text-[14px] text-slate-400 leading-relaxed max-w-sm mx-auto">{children}</p>
      {cta && (
        <button onClick={cta.onClick}
          className="mt-3 inline-flex items-center gap-1.5 text-[14px] font-semibold text-violet-300 hover:text-violet-200 min-h-11">
          <Plus className="w-4 h-4" aria-hidden="true" /> {cta.label}
        </button>
      )}
    </div>
  );
}
